import type { TFunction } from 'i18next';
import { displayPrice } from '../../../../../utils/displayPrice';
import type { Currency } from '../../../../../utils/enums/currency-type.enum';

interface Installment {
  penalty: number;
  dueDate: string | null;
  gracePeriod: number;
  isPaid?: boolean;
  paidAt?: string | null;
  currency: Currency;
}

interface RenderPenaltyProps {
  installment: Installment;
  t: TFunction;
  penaltyApplied?: boolean;
}

/**
 * Determines if a penalty has been applied (the grace period has ended)
 * If the installment was paid before the grace period ended, no penalty is applied
 */
export const isPenaltyApplied = (installment: Installment): boolean => {
  if (
    !installment.penalty ||
    installment.penalty === 0 ||
    !installment.dueDate
  ) {
    return false;
  }

  const dueDate = new Date(installment.dueDate);
  dueDate.setHours(0, 0, 0, 0);

  const gracePeriodEndDate = new Date(dueDate);
  gracePeriodEndDate.setDate(
    gracePeriodEndDate.getDate() + (installment.gracePeriod || 0)
  );

  if (installment.isPaid && installment.paidAt) {
    const paidDate = new Date(installment.paidAt);
    paidDate.setHours(0, 0, 0, 0);

    return paidDate > gracePeriodEndDate;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today > gracePeriodEndDate;
};

/**
 * Determines the row class name based on installment status
 */
export const getRowClassName = (
  isPaid: boolean,
  penaltyApplied: boolean
): string => {
  if (isPaid) return 'school-taxes__row--paid';
  if (penaltyApplied) return 'school-taxes__row--penalty';

  return '';
};

/**
 * Renders the penalty cell with optional info icon and tooltip
 */
export const renderPenaltyCell = ({
  installment,
  t,
  penaltyApplied: penaltyOverride,
}: RenderPenaltyProps) => {
  const penaltyApplied =
    penaltyOverride !== undefined
      ? penaltyOverride
      : isPenaltyApplied(installment);
  const rowClassName = getRowClassName(
    installment.isPaid || false,
    penaltyApplied
  );

  return (
    <span className={rowClassName}>
      {penaltyApplied ? (
        <>
          {displayPrice(
            installment.penalty,
            t('PAYMENTS.LABELS.BGN'),
            t('PAYMENTS.LABELS.EUR'),
            installment.currency!
          )}
        </>
      ) : (
        '-'
      )}
    </span>
  );
};
